// src/components/initial-inventory/BulkQuantityEntry.tsx
// Bulk/Consumable quantity entry form for Initial Inventory workflow
// Handles BULK_TRACKED and CONSUMABLE tracking types

import React, { useState, useEffect } from 'react';
import { Package, MapPin, Calendar, Hash } from 'lucide-react';
import { Product } from '../../services/productCatalogAPI';
import { CategoryHierarchy } from '../../types/category';
import { tenantConfigAPI } from '../../services/tenantConfigAPI';

interface BulkQuantityEntryProps {
  product: Product;
  categories: CategoryHierarchy[];
  trackingType: 'BULK_TRACKED' | 'CONSUMABLE';
  onSubmit: (
    quantity: number,
    location: string,
    subLocation?: string,
    lotNumber?: string,
    expirationDate?: string,
    notes?: string
  ) => Promise<void>;
  submitting: boolean;
}

const BulkQuantityEntry: React.FC<BulkQuantityEntryProps> = ({
  product,
  categories,
  trackingType,
  onSubmit,
  submitting
}) => {
  // Form state
  const [quantity, setQuantity] = useState<number | ''>('');
  const [location, setLocation] = useState(product.defaultLocation || '');
  const [subLocation, setSubLocation] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [notes, setNotes] = useState('');
  
  // Storage locations
  const [storageLocations, setStorageLocations] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  // Load storage locations
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locations = await tenantConfigAPI.getInventoryLocations();
        setStorageLocations(locations);
        
        // Set default if product has one and it's in the list
        if (product.defaultLocation && locations.includes(product.defaultLocation)) {
          setLocation(product.defaultLocation);
        } else if (locations.length > 0 && !location) {
          setLocation(locations[0]);
        }
      } catch (err) {
        console.error('Failed to load storage locations:', err);
      } finally {
        setLocationsLoading(false);
      }
    };
    loadLocations();
  }, [product.defaultLocation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!quantity || quantity <= 0) {
      return;
    }

    if (!location) {
      return;
    }

    await onSubmit(
      quantity,
      location,
      subLocation || undefined,
      lotNumber || undefined,
      expirationDate || undefined,
      notes || undefined
    );
    
    // Clear form on success, keep location
    setQuantity('');
    setSubLocation('');
    setLotNumber('');
    setExpirationDate('');
    setNotes('');
  };

  const getCategoryPath = (categoryId: string): string => {
    const category = categories.find(c => c.categoryId === categoryId);
    if (!category) return '';
    
    if (category.parentCategoryId) {
      const parent = categories.find(c => c.categoryId === category.parentCategoryId);
      return parent ? `${parent.name} › ${category.name}` : category.name;
    }
    
    return category.name;
  };

  const isConsumable = trackingType === 'CONSUMABLE';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Product Info Banner */}
      <div className={`rounded-lg p-3 flex items-center justify-between ${
        isConsumable ? 'bg-orange-50' : 'bg-green-50'
      }`}>
        <div className="text-sm">
          <span className={`font-medium ${isConsumable ? 'text-orange-700' : 'text-green-700'}`}>
            {isConsumable ? 'Consumable Item' : 'Bulk Tracked Item'}
          </span>
          <span className={`mx-2 ${isConsumable ? 'text-orange-600' : 'text-green-600'}`}>•</span>
          <span className={isConsumable ? 'text-orange-600' : 'text-green-600'}>
            {getCategoryPath(product.categoryId)}
          </span>
        </div>
        <span className={`text-xs px-2 py-1 rounded ${
          isConsumable 
            ? 'bg-orange-100 text-orange-700' 
            : 'bg-green-100 text-green-700'
        }`}>
          {isConsumable ? 'Returns disposed, not restocked' : 'Tracked by quantity'}
        </span>
      </div>

      {/* Quantity Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Package className="w-4 h-4 inline mr-1" />
          Quantity <span className="text-red-500">*</span>
          {product.unitOfMeasure && (
            <span className="text-gray-500 font-normal ml-1">
              ({product.unitOfMeasure})
            </span>
          )}
        </label>
        <input
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value ? parseInt(e.target.value, 10) : '')}
          placeholder="Enter quantity"
          min="1"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
          disabled={submitting}
          required
        />
        {product.minStockLevel && (
          <p className="text-xs text-gray-500 mt-1">
            Minimum stock level: {product.minStockLevel} {product.unitOfMeasure || 'units'}
          </p>
        )}
      </div>

      {/* Location Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Primary Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <MapPin className="w-4 h-4 inline mr-1" />
            Storage Location <span className="text-red-500">*</span>
          </label>
          <select
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            disabled={locationsLoading || submitting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            required
          >
            <option value="">Select location...</option>
            {storageLocations.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
          {!locationsLoading && storageLocations.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              No storage locations configured. Add them in Admin → Tenant Settings.
            </p>
          )}
        </div>

        {/* Sub-Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sub-Location <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={subLocation}
            onChange={(e) => setSubLocation(e.target.value)}
            placeholder="e.g., Shelf 3, Bin 12-A"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={submitting}
          />
        </div>
      </div>

      {/* Lot Number & Expiration Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Lot Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Hash className="w-4 h-4 inline mr-1" />
            Lot Number <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={lotNumber}
            onChange={(e) => setLotNumber(e.target.value)}
            placeholder="e.g., LOT-2024-001"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={submitting}
          />
        </div>

        {/* Expiration Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            Expiration Date <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="date"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={submitting}
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes <span className="text-gray-400">(optional)</span>
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g., Existing stock at system go-live"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          disabled={submitting}
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={submitting || !quantity || quantity <= 0 || !location}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Adding Stock...
            </>
          ) : (
            <>
              Add {quantity || 0} {product.unitOfMeasure || 'units'} to Inventory
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default BulkQuantityEntry;
